import { motion } from 'framer-motion'

import { cn } from '@/lib/utils'

export interface DonutSlice {
  label: string
  value: number
  color: string
}

interface DonutProps {
  data: DonutSlice[]
  formatValue?: (n: number) => string
  /** big label under the center total */
  centerLabel?: string
  className?: string
}

const SIZE = 200
const R = 80
const STROKE = 19
const C = { x: SIZE / 2, y: SIZE / 2 }
const EASE = [0.16, 1, 0.3, 1] as const

function polar(cx: number, cy: number, r: number, angle: number) {
  return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) }
}

function arc(a0: number, a1: number): string {
  const p0 = polar(C.x, C.y, R, a0)
  const p1 = polar(C.x, C.y, R, a1)
  const large = a1 - a0 > Math.PI ? 1 : 0
  return `M ${p0.x.toFixed(2)} ${p0.y.toFixed(2)} A ${R} ${R} 0 ${large} 1 ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`
}

/** Hand-rolled donut: arcs sweep in on mount, crisp center total, HTML legend. */
export function Donut({ data, formatValue = String, centerLabel, className }: DonutProps) {
  const total = data.reduce((s, d) => s + d.value, 0)
  const slices = data.filter((d) => d.value > 0)

  let acc = -Math.PI / 2
  const geom = slices.map((d) => {
    const frac = d.value / (total || 1)
    const a0 = acc
    const a1 = acc + frac * Math.PI * 2 - 0.03 // small gap between arcs
    acc += frac * Math.PI * 2
    return { ...d, a0, a1, frac }
  })

  return (
    <div className={cn('flex flex-col items-center gap-5 sm:flex-row sm:gap-7', className)}>
      <div className="relative shrink-0">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="h-44 w-44"
          role="img"
          aria-label="Grafic inelar"
        >
          <circle cx={C.x} cy={C.y} r={R} fill="none" stroke="var(--muted)" strokeWidth={STROKE} />
          {total === 0
            ? null
            : geom.map((g, i) => (
                <motion.path
                  key={g.label}
                  d={arc(g.a0, g.a1)}
                  fill="none"
                  stroke={g.color}
                  strokeWidth={STROKE}
                  strokeLinecap="round"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 0.7, delay: 0.1 + i * 0.12, ease: EASE }}
                />
              ))}
        </svg>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-mono text-base font-semibold tracking-tight text-amber-ink nums">
            {formatValue(total)}
          </span>
          {centerLabel && (
            <span className="mt-0.5 text-[11px] uppercase tracking-wide text-muted-foreground">
              {centerLabel}
            </span>
          )}
        </div>
      </div>

      <ul className="w-full space-y-2.5">
        {data.map((d) => {
          const pct = total ? Math.round((d.value / total) * 100) : 0
          return (
            <li key={d.label} className="flex items-center gap-2.5 text-sm">
              <span
                className="size-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: d.color }}
                aria-hidden
              />
              <span className="min-w-0 flex-1 truncate text-foreground">{d.label}</span>
              <span className="font-mono text-xs text-muted-foreground nums">{pct}%</span>
              <span className="w-24 text-right font-mono text-xs font-medium text-amber-ink nums">
                {formatValue(d.value)}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
